import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface LockMessage {
  id: number;
  created_at: string;
  title: string;
  line1: string;
  line2: string | null;
  line3: string | null;
  footer: string | null;
  is_default: boolean;
}

interface LockMessagePayload {
  title: string;
  line1: string;
  line2?: string;
  line3?: string;
  footer?: string;
}

export function useLockMessages() {
  const [messages, setMessages] = useState<LockMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from('lock_messages')
        .select('id, created_at, title, line1, line2, line3, footer, is_default')
        .order('is_default', { ascending: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        throw fetchError;
      }

      setMessages((data as LockMessage[]) || []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch lock messages');
    } finally {
      setLoading(false);
    }
  };

  const createMessage = async (payload: LockMessagePayload) => {
    try {
      const { data, error: insertError } = await supabase
        .from('lock_messages')
        .insert({
          title: payload.title.trim(),
          line1: payload.line1.trim(),
          line2: payload.line2?.trim() || null,
          line3: payload.line3?.trim() || null,
          footer: payload.footer?.trim() || null
        })
        .select('id, created_at, title, line1, line2, line3, footer, is_default')
        .single();

      if (insertError) {
        throw insertError;
      }

      setMessages((prev) => [data as LockMessage, ...prev]);
      return { success: true, data: data as LockMessage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create lock message';
      setError(message);
      return { success: false, error: message };
    }
  };

  const updateMessage = async (id: number, payload: LockMessagePayload) => {
    try {
      const { data, error: updateError } = await supabase
        .from('lock_messages')
        .update({
          title: payload.title.trim(),
          line1: payload.line1.trim(),
          line2: payload.line2?.trim() || null,
          line3: payload.line3?.trim() || null,
          footer: payload.footer?.trim() || null
        })
        .eq('id', id)
        .select('id, created_at, title, line1, line2, line3, footer, is_default')
        .single();

      if (updateError) {
        throw updateError;
      }

      setMessages((prev) => prev.map((item) => (item.id === id ? (data as LockMessage) : item)));
      return { success: true, data: data as LockMessage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update lock message';
      setError(message);
      return { success: false, error: message };
    }
  };

  const deleteMessage = async (id: number) => {
    try {
      const { error: deleteError } = await supabase
        .from('lock_messages')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      setMessages((prev) => prev.filter((item) => item.id !== id));
      return { success: true };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete lock message';
      setError(message);
      return { success: false, error: message };
    }
  };

  const setDefaultMessage = async (id: number) => {
    try {
      const { error: clearError } = await supabase
        .from('lock_messages')
        .update({ is_default: false })
        .neq('id', id);

      if (clearError) {
        throw clearError;
      }

      const { data, error: setError } = await supabase
        .from('lock_messages')
        .update({ is_default: true })
        .eq('id', id)
        .select('id, created_at, title, line1, line2, line3, footer, is_default')
        .single();

      if (setError) {
        throw setError;
      }

      setMessages((prev) => {
        const updated = prev.map((item) => ({
          ...item,
          is_default: item.id === id
        }));

        const updatedDefault = data as LockMessage;
        return updated.map((item) => (item.id === id ? updatedDefault : item));
      });

      return { success: true, data: data as LockMessage };
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set default lock message';
      setError(message);
      return { success: false, error: message };
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  return {
    messages,
    loading,
    error,
    fetchMessages,
    createMessage,
    updateMessage,
    deleteMessage,
    setDefaultMessage,
    clearError: () => setError(null)
  };
}
